import React from 'react';

/** Full-page skeleton used as Suspense fallback for route-level lazy loading */
export const PageSkeleton: React.FC = () => (
  <div className="p-4 md:p-12 w-full space-y-12 animate-pulse">
    <div className="space-y-3">
      <div className="h-8 w-64 bg-pt-forest/5 rounded-2xl" />
      <div className="h-4 w-96 bg-pt-forest/5 rounded-xl" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
      {[...Array(4)].map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
    <TableSkeleton rows={5} />
  </div>
);

/** Dashboard-specific skeleton matching the GridStack widget layout */
export const DashboardSkeleton: React.FC = () => (
  <div className="db-page animate-pulse" style={{ padding: '24px 32px' }}>
    {/* Header: project name + stats bar */}
    <div className="space-y-4 mb-8">
      <div className="h-7 w-48 bg-pt-forest/5 rounded-xl" />
      <div className="flex gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex-1 h-16 bg-pt-forest/5 rounded-2xl" />
        ))}
      </div>
    </div>
    {/* Widget grid: 3 columns mimicking GridStack layout */}
    <div className="grid grid-cols-3 gap-3">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-2xl border border-pt-forest/5 p-5 space-y-3"
          style={{ minHeight: 280 }}
        >
          <div className="flex items-center justify-between">
            <div className="h-5 w-28 bg-pt-forest/5 rounded-lg" />
            <div className="h-5 w-5 bg-pt-forest/5 rounded" />
          </div>
          <div className="h-3 w-40 bg-pt-forest/5 rounded" />
          <div className="flex-1 flex items-center justify-center" style={{ minHeight: 160 }}>
            <div className="h-24 w-24 bg-pt-forest/5 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Card placeholder with shimmer */
export const CardSkeleton: React.FC = () => (
  <div className="bg-white rounded-[32px] border border-pt-forest/5 p-8 space-y-4 animate-pulse">
    <div className="h-12 w-12 bg-pt-forest/5 rounded-2xl" />
    <div className="h-6 w-20 bg-pt-forest/5 rounded-xl" />
    <div className="h-3 w-32 bg-pt-forest/5 rounded-lg" />
  </div>
);

/** Table placeholder with configurable row count */
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="bg-white rounded-[40px] border border-pt-forest/5 overflow-hidden animate-pulse">
    <div className="bg-pt-cream/50 px-4 md:px-10 py-6 flex gap-16">
      <div className="h-3 w-24 bg-pt-forest/5 rounded" />
      <div className="h-3 w-20 bg-pt-forest/5 rounded" />
      <div className="h-3 w-28 bg-pt-forest/5 rounded" />
      <div className="h-3 w-16 bg-pt-forest/5 rounded ml-auto" />
    </div>
    {[...Array(rows)].map((_, i) => (
      <div
        key={i}
        className="px-4 md:px-10 py-6 flex items-center gap-4 border-t border-pt-forest/5"
      >
        <div className="w-12 h-12 bg-pt-forest/5 rounded-2xl shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-40 bg-pt-forest/5 rounded-lg" />
          <div className="h-3 w-24 bg-pt-forest/5 rounded" />
        </div>
        <div className="h-8 w-8 bg-pt-forest/5 rounded-xl" />
      </div>
    ))}
  </div>
);
